import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import {Cell, ColumnConstraint, TableColumnType} from 'notebook/tables/TableConstants';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {Optional} from 'util/Types';

/**
 * Returns true if the column type indicates that cell text values should be treated as text, not numbers
 * for the purpose of parsing.
 */
export function isRawTextColumn(columnConstraint: Optional<ColumnConstraint>): boolean {
  return (
    columnConstraint?.columnDefinition != null &&
    ([
      TableColumnType.STRING,
      TableColumnType.BIOENTITY_NAME,
      TableColumnType.MEASUREMENT_AMOUNT,
      TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT,
      TableColumnType.INGREDIENT_CATALOG_ID,
      TableColumnType.INGREDIENT_NOTES,
    ].includes(columnConstraint.columnDefinition.type) ||
      (columnConstraint.columnDefinition.type === TableColumnType.CUSTOM &&
        columnConstraint.columnDefinition.customColumnConfig.dataType === CustomColumnDataType.TEXT) ||
      (columnConstraint.columnDefinition.type === TableColumnType.TAG_VALUE &&
        [FieldType.TEXT, FieldType.LONG_TEXT].includes(columnConstraint.columnDefinition.tagSchemaFieldType)))
  );
}

/**
 * Returns true if the column type represents a storable type.
 *
 * This is used when parsing to treat manually entered text explicitly as text and
 * to not try and parse numerically.
 */
function isStorableColumn(columnConstraint: Optional<ColumnConstraint>): boolean {
  return (
    columnConstraint?.columnDefinition != null &&
    [TableColumnType.CONTAINERS, TableColumnType.BOX, TableColumnType.PLATE].includes(
      columnConstraint.columnDefinition.type
    )
  );
}

/**
 * Parse cell contents into a typedValue.
 */
export default function parseTypedValue(
  cell: Cell,
  columnConstraint: Optional<ColumnConstraint>
): TypedValue | null {
  const {typedValue, text} = cell;
  if (typedValue != null) {
    return typedValue;
  }
  if (text && (isRawTextColumn(columnConstraint) || isStorableColumn(columnConstraint))) {
    return {
      type: BaseTypedValueType.STRING,
      rawValue: text,
    };
  }
  return null;
}
